import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  GbpersonService,
  LastRechtGruppeEntryMeta,
  LastRechtPersonEntryMeta,
  PersonGbListEntryMeta,
  PersonGbMasterDetailEntryMeta,
  PersonGbVersionDetailEntryMeta,
  StandardrechtMasterDetailEntryMeta
} from '../../../../core/gb-api';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TimepointService } from '../../../../core/services/timepoint.service';
import { AppConfigService } from '../../../../core/services/app-config.service';
import { ComparatorService } from '../../../../shared/services/comparator-service';
import { HistoryMode } from '../../../../core/services/HistoryMode';
import { Subscription } from 'rxjs';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { HistoryDirective } from '../../../../shared/directives/history.directive';
import { KompaktGrundstueckComponent } from '../../../../shared/kompaktdarstellung/grundstueck/kompakt-grundstueck.component';
import { KompaktTimepointComponent } from '../../../../shared/kompaktdarstellung/timepoint/kompakt-timepoint.component';
import { RechtsgrundComponent } from '../../../../shared/kompaktdarstellung/rechtsgrund/rechtsgrund.component';
import { KompaktPersongbComponent } from '../../../../shared/kompaktdarstellung/persongb/kompakt-persongb.component';
import { BelastungArtPipe } from '../../../../shared/pipes/simple/belastung-art.pipe';
import { OrderByGrundstuecknummerPipe } from '../../../../shared/pipes/order/order-by-grundstuecknummer.pipe';
import AuskunftsperreEnum = PersonGbListEntryMeta.AuskunftsperreEnum;

@Component({
  selector: 'cap-standardrecht-belastungen',
  templateUrl: './standardrecht-belastungen.component.html',
  styleUrls: ['./standardrecht-belastungen.component.scss'],
  imports: [
    TranslatePipe,
    NgClass,
    HistoryDirective,
    KompaktGrundstueckComponent,
    KompaktTimepointComponent,
    RechtsgrundComponent,
    KompaktPersongbComponent,
    NgTemplateOutlet,
    BelastungArtPipe,
    OrderByGrundstuecknummerPipe
  ]
})
export class StandardrechtBelastungenComponent implements OnInit, OnDestroy {
  standardRechtMasterVm: StandardrechtMasterVm;
  protected readonly PersonGbListEntryMeta = PersonGbListEntryMeta;
  protected readonly AuskunftsperreEnum = AuskunftsperreEnum;
  protected readonly PersonGbMasterDetailEntryMeta = PersonGbMasterDetailEntryMeta;
  private langChangeSubscription: Subscription;
  private subscriptions: Subscription;

  language: string;

  constructor(
    private translateService: TranslateService,
    private comparatorService: ComparatorService,
    private timepointService: TimepointService,
    private appConfigService: AppConfigService,
    private gbpersonService: GbpersonService
  ) {
    this.langChangeSubscription = this.translateService.onLangChange.subscribe((language) => {
      this.language = language.lang;
    });
  }

  ngOnInit() {
    keysToTranslate<string>(['RechtsgrundArt', 'GrundstückPerson', 'Rechtsgrund']);
  }

  @Input()
  set recht(value: StandardrechtMasterDetailEntryMeta) {
    this.standardRechtMasterVm = new StandardrechtMasterVm(value, this.getViewModelCtx(), this.subscriptions);
  }

  getAuskunftsperre(personGbHasAuskunftssperre: PersonGbMasterDetailEntryMeta.HasAuskunftssperreEnum): AuskunftsperreEnum {
    return personGbHasAuskunftssperre === PersonGbMasterDetailEntryMeta.HasAuskunftssperreEnum.AUSKUNFT_SPERRE
      ? AuskunftsperreEnum.AUSKUNFT_SPERRE
      : AuskunftsperreEnum.KEINE;
  }

  private getViewModelCtx() {
    return <ViewModelCtx>{
      timepointId: this.timepointService.timepointCtx.timepointId,
      timepoint: this.timepointService.timepointCtx.timepointIso,
      einstellungHistorischeDaten: this.appConfigService.einstellungHistorischeDaten,
      personService: this.gbpersonService,
      translateService: this.translateService,
      comparatorService: this.comparatorService
    };
  }

  ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe();
    this.subscriptions?.unsubscribe();
  }
}

interface ViewModelCtx {
  translateService: TranslateService;
  comparatorService: ComparatorService;
  personService: GbpersonService;
  einstellungHistorischeDaten: HistoryMode.HistoryMode;
  timepoint: string;
  timepointId: string;
}

class StandardrechtMasterVm {
  lastRechtGruppenVm: Array<LastRechtGruppeVm>;

  constructor(
    public object: StandardrechtMasterDetailEntryMeta,
    ctx: ViewModelCtx,
    subscription: Subscription
  ) {
    this.lastRechtGruppenVm = new Array<LastRechtGruppeVm>();
    for (let i = 0; i < object.lastRechtGruppen.length; i++) {
      this.lastRechtGruppenVm.push(
        new LastRechtGruppeVm(object.lastRechtGruppen[i], i === 0, i === object.lastRechtGruppen.length - 1, ctx, subscription)
      );
    }
    this.lastRechtGruppenVm = this.lastRechtGruppenVm.sort((a, b) => ctx.comparatorService.lastRechtComparator(a.object, b.object));
  }
}

class LastRechtGruppeVm {
  lastRechtPersonenVm: Array<LastRechtPersonVm>;

  constructor(
    public object: LastRechtGruppeEntryMeta,
    public isFirstGlaeubiger,
    public isLastGlaeubiger: boolean,
    ctx: ViewModelCtx,
    subscription: Subscription
  ) {
    this.lastRechtPersonenVm = object.personen.map((m) => new LastRechtPersonVm(m, ctx, subscription));
  }
}

class LastRechtPersonVm {
  personGbMasterVm: PersonGbMasterVm;
  private sub: Subscription;

  constructor(
    public object: LastRechtPersonEntryMeta,
    ctx: ViewModelCtx,
    subscriptions: Subscription
  ) {
    this.sub = ctx.personService
      .loadPersonGbById(ctx.einstellungHistorischeDaten, object.personGbMasterId.id, null, ctx.timepoint, ctx.timepointId)
      .subscribe((m) => (this.personGbMasterVm = new PersonGbMasterVm(m, ctx)));
    subscriptions = this.sub;
  }
}

class PersonGbMasterVm {
  versionenVm: PersonGbVersionVm[];

  constructor(
    public object: PersonGbMasterDetailEntryMeta,
    ctx: ViewModelCtx
  ) {
    this.versionenVm = new Array<PersonGbVersionVm>();
    for (let i = 0; i < object.versions.length; i++) {
      this.versionenVm.push(new PersonGbVersionVm(object.versions[i], i === 0, i === object.versions.length - 1, ctx));
    }
  }
}

class PersonGbVersionVm {
  constructor(
    public object: PersonGbVersionDetailEntryMeta,
    public isFirstVersion,
    public isLastVersion: boolean,
    ctx: ViewModelCtx
  ) {}
}
